import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaArrowRight,
    FaSearch,
    FaSort,
    FaSortDown,
    FaSortUp,
    FaSync,
    FaMagic,
    FaTimes,
    FaPlay,
} from 'react-icons/fa';
import PodcastOptionsModal from './PodcastOptionsModal';
import PointsConfirmationModal from './PointsConfirmationModal';
import { regeneratePodcast } from './FileUploader';
import { QUIZMASTER_API } from '../config';
import { formatToLocalDateTime, getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';
import FloatingChat from './FloatingChat';

const PodcastList = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [podcastData, setPodcastData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [apiStatus, setApiStatus] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const location = useLocation();
    const navigate = useNavigate();
    const document = location.state?.selectedDocument;
    const regeneratePromiseRef = useRef(null);
    const costConfirmationResolveRef = useRef(null);
    const [costConfirmation, setCostConfirmation] = useState(null);

    const startTimer = useCallback(() => null, []);

    const stopTimer = useCallback(() => { }, []);

    const handleRegenerationLoading = useCallback((value) => {
        setIsRegenerating(Boolean(value));
    }, []);

    useEffect(() => {
        return () => {
            if (costConfirmationResolveRef.current) {
                costConfirmationResolveRef.current(false);
                costConfirmationResolveRef.current = null;
            }
        };
    }, []);

    const fetchPodcasts = useCallback(async ({ showSpinner = false } = {}) => {
        if (!document) {
            setError('No document selected.');
            if (showSpinner) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
            return;
        }

        setError(null);
        if (showSpinner) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        let authToken = accessToken;

        try {
            const podcastUrl = `${QUIZMASTER_API}/get-podcasts-by-document/?document_id=${document.id}`;
            let podcastResponse = await fetch(podcastUrl, {
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
            });

            if (!podcastResponse.ok) {
                if (podcastResponse.status === 401 && supabaseRefreshToken) {
                    const refreshResponse = await fetch(`${QUIZMASTER_API}/auth/refresh`, {
                        method: 'POST',
                        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                        body: JSON.stringify({ refresh_token: supabaseRefreshToken }),
                    });
                    if (!refreshResponse.ok) {
                        throw new Error('Token refresh failed');
                    }
                    const { access_token: newAccessToken, refresh_token: newRefreshToken } = await refreshResponse.json();
                    onSignIn({ user_id: document.user_id, accessToken: newAccessToken, refreshToken: newRefreshToken });
                    authToken = newAccessToken;
                    podcastResponse = await fetch(podcastUrl, {
                        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
                    });
                    if (!podcastResponse.ok) {
                        throw new Error('Failed to fetch podcasts after token refresh');
                    }
                } else {
                    throw new Error('Failed to fetch podcasts');
                }
            }

            const podcastsData = await podcastResponse.json();
            const podcasts = podcastsData.podcasts || [];

            if (podcasts.length === 0) {
                setPodcastData([]);
                return;
            }

            setPodcastData(podcasts);
        } catch (err) {
            console.error('[PodcastList] Error fetching podcasts:', err);
            setError(err.message);
        } finally {
            if (showSpinner) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    }, [document, accessToken, supabaseRefreshToken, onSignIn]);

    useEffect(() => {
        fetchPodcasts({ showSpinner: true });
    }, [fetchPodcasts]);

    const refreshAfterRegen = useCallback(() => {
        fetchPodcasts({ showSpinner: false });
    }, [fetchPodcasts]);

    const filteredPodcasts = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return podcastData;
        }

        return podcastData.filter((podcast) => {
            const tokens = [
                podcast.title,
                podcast.id,
                podcast.duration_estimate_minutes,
            ];

            return tokens.some((value) =>
                value && String(value).toLowerCase().includes(term)
            );
        });
    }, [podcastData, searchTerm]);

    const sortedPodcasts = useMemo(() => {
        const { key, direction } = sortConfig;
        if (!key) {
            return filteredPodcasts;
        }

        const multiplier = direction === 'asc' ? 1 : -1;

        return [...filteredPodcasts].sort((a, b) => {
            switch (key) {
                case 'title':
                    return multiplier * String(a.title || '').localeCompare(String(b.title || ''));
                case 'duration_estimate_minutes':
                    return multiplier * ((a.duration_estimate_minutes ?? 0) - (b.duration_estimate_minutes ?? 0));
                case 'created_at':
                default: {
                    const aDate = getUtcTimestamp(a.created_at);
                    const bDate = getUtcTimestamp(b.created_at);
                    return multiplier * ((aDate ?? 0) - (bDate ?? 0));
                }
            }
        });
    }, [filteredPodcasts, sortConfig]);

    const handleSort = useCallback((key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }

            const defaultDirection = key === 'created_at' || key === 'duration_estimate_minutes' ? 'desc' : 'asc';
            return { key, direction: defaultDirection };
        });
    }, []);

    const getSortIcon = useCallback((key) => {
        if (sortConfig.key !== key) {
            return <FaSort size={12} />;
        }
        return sortConfig.direction === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
    }, [sortConfig]);

    const formatDate = useCallback(
        (dateStr) =>
            formatToLocalDateTime(dateStr, {
                fallback: 'N/A',
                includeSeconds: false,
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZoneName: null,
            }),
        []
    );

    const handleRefreshClick = useCallback(() => {
        fetchPodcasts({ showSpinner: false });
    }, [fetchPodcasts]);

    const confirmRegenerationCost = useCallback(({ estimatedPoints }) => {
        return new Promise((resolve) => {
            if (costConfirmationResolveRef.current) {
                costConfirmationResolveRef.current(false);
            }
            costConfirmationResolveRef.current = resolve;
            setCostConfirmation({ estimatedPoints });
        });
    }, []);

    const handleCostModalConfirm = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationResolveRef.current) {
            costConfirmationResolveRef.current(true);
            costConfirmationResolveRef.current = null;
        }
    }, []);

    const handleCostModalCancel = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationResolveRef.current) {
            costConfirmationResolveRef.current(false);
            costConfirmationResolveRef.current = null;
        }
    }, []);

    const handleRegeneratePodcast = useCallback(async () => {
        if (!document) {
            setError('No document selected.');
            return;
        }
        try {
            setRegenerateModalVisible(true);
            const { durationMinutes } = await new Promise((resolve, reject) => {
                regeneratePromiseRef.current = { resolve, reject };
            });
            await regeneratePodcast({
                documentId: document.id,
                tokenCount: document.token_count,
                filename: document.filename,
                setApiStatus,
                startTimer,
                stopTimer,
                setLoading: handleRegenerationLoading,
                refreshDocuments: refreshAfterRegen,
                accessToken,
                supabaseRefreshToken,
                onSignIn,
                durationMinutes,
                userId: document.user_id,
                confirmCost: confirmRegenerationCost,
            });
        } catch (error) {
            if (error.message !== 'User canceled') {
                console.error('[PodcastList] Error regenerating podcast:', error);
                setApiStatus(error.message || 'An error occurred while regenerating the podcast.');
            }
        }
    }, [
        document,
        accessToken,
        supabaseRefreshToken,
        onSignIn,
        startTimer,
        stopTimer,
        handleRegenerationLoading,
        refreshAfterRegen,
        confirmRegenerationCost,
    ]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading podcasts...</p>
            </div>
        );
    }

    if (error || !document) {
        return (
            <div className="error-container">
                <p>{error || 'No document selected.'}</p>
                <button className="button confirm-button" onClick={() => navigate('/')}>
                    Back to Home
                </button>
            </div>
        );
    }

    const statusSegments = [];
    if (apiStatus) {
        statusSegments.push(apiStatus);
    }
    if (refreshing) {
        statusSegments.push('Refreshing podcast list...');
    }
    const statusMessage = statusSegments.join(' | ') || (isRegenerating ? 'Working on your podcast...' : '');

    const hasPodcasts = Array.isArray(podcastData) && podcastData.length > 0;

    return (
        <div className="scroll-container">
            <div className="document-list-wrapper podcast-list-wrapper">
                <div className="podcast-list-header">
                    <div className="podcast-list-header__titles">
                        <h2 className="sub-header">Podcasts for {document.filename}</h2>
                        <p className="podcast-list-header__subtitle">
                            Listen to AI-generated podcasts based on your document content.
                        </p>
                    </div>
                </div>

                {statusMessage && (
                    <div className="api-status-container">
                        {(isRegenerating || refreshing) && <div className="spinner spinner--inline" aria-hidden="true"></div>}
                        <p className="api-status-text">{statusMessage}</p>
                    </div>
                )}

                <div className="search-container podcast-list-search">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" aria-hidden="true" />
                        <input
                            type="text"
                            className="search-input search-input--large"
                            placeholder="Search by podcast title..."
                            aria-label="Search podcasts"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="clear-button"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                                aria-label="Clear search"
                            >
                                <FaTimes className="clear-button-icon" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className="button button--icon podcast-regenerate-button"
                        onClick={handleRegeneratePodcast}
                        disabled={loading || refreshing || isRegenerating}
                        title="Generate a fresh AI-powered podcast for this document"
                        aria-label="Regenerate new podcast"
                    >
                        <span className="podcast-regenerate-button__icon" aria-hidden="true">
                            <FaMagic />
                        </span>
                        <span className="podcast-regenerate-button__badge" aria-hidden="true">NEW</span>
                    </button>
                </div>

                <div className="document-list-container">
                    <div className="document-list-controls podcast-list-controls">
                        <div className="document-sort">
                            <span className="document-sort__label">Sort by</span>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('created_at')}
                                aria-pressed={sortConfig.key === 'created_at'}
                            >
                                Most Recent {getSortIcon('created_at')}
                            </button>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('title')}
                                aria-pressed={sortConfig.key === 'title'}
                            >
                                Title {getSortIcon('title')}
                            </button>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('duration_estimate_minutes')}
                                aria-pressed={sortConfig.key === 'duration_estimate_minutes'}
                            >
                                Duration {getSortIcon('duration_estimate_minutes')}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="button button--ghost podcast-refresh-button"
                            onClick={handleRefreshClick}
                            disabled={refreshing || loading || isRegenerating}
                        >
                            <FaSync aria-hidden="true" />
                            <span>{refreshing ? 'Refreshing...' : 'Refresh List'}</span>
                        </button>
                    </div>

                    {hasPodcasts ? (
                        <div className="document-grid podcast-grid">
                            {sortedPodcasts.map((podcast, index) => {
                                const podcastKey = podcast.id || `${podcast.title}-${podcast.created_at || index}`;

                                return (
                                    <div key={podcastKey} className="document-card podcast-card">
                                        <div className="podcast-card__header">
                                            <div className="podcast-card__title-group">
                                                <h3 className="podcast-card__title">{podcast.title}</h3>
                                                <FaPlay aria-hidden="true" className="document-card__icon" />
                                            </div>
                                        </div>
                                        <div className="podcast-card__meta">
                                            <span className="podcast-card__label">Duration</span>
                                            <span className="podcast-card__value">{podcast.duration_estimate_minutes} min</span>
                                        </div>
                                        <div className="podcast-card__meta">
                                            <span className="podcast-card__label">Created</span>
                                            <span className="podcast-card__value">{formatDate(podcast.created_at)}</span>
                                        </div>
                                        <div className="podcast-card__actions">
                                            <button
                                                className="button podcast-card__button"
                                                onClick={() => {
                                                    navigate('/podcast', {
                                                        state: {
                                                            selectedPodcast: podcast,
                                                            selectedDocument: document,
                                                        },
                                                    });
                                                }}
                                            >
                                                Play Podcast
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="document-empty-state podcast-empty-state">
                            {searchTerm
                                ? 'No podcasts match your search.'
                                : `No podcasts found for ${document.filename}. Use "Regenerate Podcast" to create a new podcast.`}
                        </div>
                    )}
                </div>

                <div className="podcast-list-footer">
                    <button className="button" onClick={() => navigate('/')}>
                        Back to Documents
                    </button>
                </div>
            </div>

            <PodcastOptionsModal
                visible={regenerateModalVisible}
                onConfirm={(options) => {
                    setRegenerateModalVisible(false);
                    regeneratePromiseRef.current?.resolve(options);
                    regeneratePromiseRef.current = null;
                }}
                onCancel={() => {
                    setRegenerateModalVisible(false);
                    regeneratePromiseRef.current?.reject(new Error('User canceled'));
                    regeneratePromiseRef.current = null;
                }}
            />
            <PointsConfirmationModal
                visible={Boolean(costConfirmation)}
                estimatedPoints={costConfirmation?.estimatedPoints ?? 0}
                numQuestions={1} // Fixed for podcast
                onConfirm={handleCostModalConfirm}
                onCancel={handleCostModalCancel}
                title="Confirm podcast regeneration"
                confirmText="Yes, regenerate"
            />
            {document && (
                <FloatingChat
                    documentId={document.id}
                    filename={document.filename}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

export default PodcastList;